<template>
  <div class="weekly-analysis">
    <el-card class="filter-card" shadow="never">
      <div slot="header" class="card-header">
        <span>周度波动分析</span>
      </div>
      <el-form :inline="true" :model="query" size="small" class="query-form">
        <el-form-item label="统计日期">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
            @change="handleDateChange"
          />
        </el-form-item>
        <el-form-item label="客户编码">
          <el-select
            v-model="query.balanceCustNo"
            clearable
            filterable
            :filter-method="filterCustomers"
            :loading="customerLoading"
            placeholder="请选择客户"
            style="width: 200px"
          >
            <el-option
              v-for="item in customerOptions"
              :key="item.custNo"
              :label="formatOptionLabel(item.custName, item.custNo)"
              :value="item.custNo"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="煤种编码">
          <el-select
            v-model="query.varNo"
            clearable
            filterable
            :filter-method="filterVars"
            :loading="varLoading"
            placeholder="请选择煤种"
            style="width: 200px"
          >
            <el-option
              v-for="item in varOptions"
              :key="item.varNo"
              :label="formatOptionLabel(item.varName, item.varNo)"
              :value="item.varNo"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="车牌号">
          <el-input v-model="query.vehicleNo" clearable placeholder="请输入车牌号" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" :loading="loading" @click="loadData">
            查询
          </el-button>
          <el-button icon="el-icon-refresh-left" @click="resetQuery">
            重置
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-row :gutter="12" class="summary-row">
      <el-col v-for="item in summaryCards" :key="item.label" :xs="12" :sm="12" :md="6">
        <el-card class="summary-card" shadow="never">
          <div class="summary-label">{{ item.label }}</div>
          <div class="summary-value" :class="item.className">
            {{ item.value }}
            <span>{{ item.unit }}</span>
          </div>
          <div class="summary-sub">{{ item.sub || "-" }}</div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="12">
      <el-col :xs="24" :md="16">
        <el-card class="content-card" shadow="never" v-loading="loading">
          <div slot="header" class="card-header">
            <span>周趋势</span>
          </div>
          <template v-if="trendList.length">
            <div class="trend-chart">
              <div
                v-for="(item, index) in trendList"
                :key="getTrendKey(item, index)"
                class="bar-item"
              >
                <div
                  class="bar"
                  :class="{ abnormal: item.abnormalFlag }"
                  :style="{ height: getBarHeight(item.totalQty) }"
                >
                  <span>{{ formatNumber(item.totalQty) }}</span>
                </div>
                <div class="bar-label">{{ formatText(item.weekBucket) }}</div>
              </div>
            </div>
            <div class="trend-legend">
              <span><i class="legend-dot legend-dot--abnormal"></i>异常</span>
              <span><i class="legend-dot legend-dot--normal"></i>正常</span>
            </div>
          </template>
          <el-empty v-else description="暂无周度趋势数据" />
        </el-card>
      </el-col>
      <el-col :xs="24" :md="8">
        <el-card class="content-card" shadow="never" v-loading="loading">
          <div slot="header" class="card-header">
            <span>异常周</span>
            <span class="header-tip">{{ abnormalWeeks.length }} 周</span>
          </div>
          <el-table
            v-if="abnormalWeeks.length"
            :data="abnormalWeeks"
            size="small"
            height="280"
            border
          >
            <el-table-column label="周" width="90">
              <template slot-scope="{ row }">{{ formatText(row.weekBucket) }}</template>
            </el-table-column>
            <el-table-column label="出库量(吨)" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.totalQty) }}</template>
            </el-table-column>
            <el-table-column label="异常" width="80" align="center">
              <template slot-scope="{ row }">
                <el-tag type="danger" size="mini">异常</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="原因" min-width="220" show-overflow-tooltip>
              <template slot-scope="{ row }">{{ formatText(row.abnormalReason) }}</template>
            </el-table-column>
          </el-table>
          <el-empty v-else description="暂无异常周" />
        </el-card>
      </el-col>
    </el-row>

    <el-card class="content-card table-card" shadow="never">
      <div slot="header" class="card-header">
        <span>周度明细</span>
      </div>
      <el-table
        :data="pagedTrendList"
        :loading="loading"
        size="small"
        border
        stripe
        empty-text="暂无周度明细"
      >
        <el-table-column label="周" min-width="100">
          <template slot-scope="{ row }">{{ formatText(row.weekBucket) }}</template>
        </el-table-column>
        <el-table-column label="周起止" min-width="190">
          <template slot-scope="{ row }">{{ formatText(row.weekStart) }} 至 {{ formatText(row.weekEnd) }}</template>
        </el-table-column>
        <el-table-column label="出库量(吨)" min-width="120" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.totalQty) }}</template>
        </el-table-column>
        <el-table-column label="车辆数" width="90" align="right">
          <template slot-scope="{ row }">{{ formatCount(row.vehicleCount) }}</template>
        </el-table-column>
        <el-table-column label="客户数" width="90" align="right">
          <template slot-scope="{ row }">{{ formatCount(row.customerCount) }}</template>
        </el-table-column>
        <el-table-column label="煤种数" width="90" align="right">
          <template slot-scope="{ row }">{{ formatCount(row.varCount) }}</template>
        </el-table-column>
        <el-table-column label="环比" width="100" align="right">
          <template slot-scope="{ row }">
            <span :class="getRateClass(row.wowChangeRate)">{{ formatPercent(row.wowChangeRate) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="均值偏离" width="110" align="right">
          <template slot-scope="{ row }">
            <span :class="getRateClass(row.avgDeviationRate)">{{ formatPercent(row.avgDeviationRate) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="异常" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.abnormalFlag ? 'danger' : 'success'" size="mini">
              {{ row.abnormalFlag ? "异常" : "正常" }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="原因" min-width="180" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ formatText(row.abnormalReason) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="110" fixed="right" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" @click="openDetail(row)">检斤明细</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-wrap">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :current-page.sync="page.currentPage"
          :page-size.sync="page.pageSize"
          :page-sizes="[10, 20, 50]"
          :total="trendList.length"
        />
      </div>
    </el-card>

    <el-drawer
      title="检斤明细"
      :visible.sync="detailVisible"
      size="70%"
      append-to-body
      @close="closeDetail"
    >
      <div class="drawer-body">
        <el-table
          :data="detailRows"
          :loading="detailLoading"
          size="small"
          border
          stripe
          empty-text="暂无检斤明细"
        >
          <el-table-column label="检斤编号" min-width="170">
            <template slot-scope="{ row }">{{ formatText(row.id) }}</template>
          </el-table-column>
          <el-table-column label="车牌号" min-width="110">
            <template slot-scope="{ row }">{{ formatText(row.vehicleNo) }}</template>
          </el-table-column>
          <el-table-column label="客户" min-width="150" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ formatName(row.balanceCustName, row.balanceCustNo) }}</template>
          </el-table-column>
          <el-table-column label="煤种" min-width="140" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ formatName(row.varName, row.varNo) }}</template>
          </el-table-column>
          <el-table-column label="出库量(吨)" min-width="110" align="right">
            <template slot-scope="{ row }">{{ formatNumber(row.balanceWeight) }}</template>
          </el-table-column>
          <el-table-column label="重车时间" min-width="170">
            <template slot-scope="{ row }">{{ formatText(row.grossTime) }}</template>
          </el-table-column>
        </el-table>
        <div class="pagination-wrap">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next"
            :current-page.sync="detailPage.currentPage"
            :page-size.sync="detailPage.pageSize"
            :page-sizes="[10, 20, 50]"
            :total="detailPage.total"
            @current-change="loadDetail"
            @size-change="loadDetail"
          />
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { getCustomerOptions, getVarOptions, getWeeklyAnalysis, getWeeklyDetailPage } from "./api";
import { unwrapDictList } from "@/util/dict-list";

function unwrapList(res) {
  return unwrapDictList(res);
}

export default {
  name: "automobile-weekly-analysis",
  data () {
    return {
      loading: false,
      detailLoading: false,
      detailVisible: false,
      activeWeek: null,
      dateRange: this.getDefaultDateRange(),
      query: {
        balanceCustNo: "",
        varNo: "",
        vehicleNo: "",
      },
      analysis: {},
      trendList: [],
      abnormalWeeks: [],
      customerLoading: false,
      varLoading: false,
      customerOptions: [],
      varOptions: [],
      defaultCustomerOptions: [],
      defaultVarOptions: [],
      customerSearchTimer: null,
      varSearchTimer: null,
      page: {
        currentPage: 1,
        pageSize: 10,
      },
      detailPage: {
        currentPage: 1,
        pageSize: 10,
        total: 0,
      },
      detailRows: [],
    };
  },
  computed: {
    pagedTrendList () {
      const start = (this.page.currentPage - 1) * this.page.pageSize;
      return this.trendList.slice(start, start + this.page.pageSize);
    },
    summaryCards () {
      return [
        { label: "区间出库量", value: this.formatNumber(this.analysis.totalQty), unit: "吨", sub: `${this.analysis.weekCount || 0} 周` },
        { label: "周均出库量", value: this.formatNumber(this.analysis.avgWeeklyQty), unit: "吨", sub: "自然周均值" },
        { label: "最大波动", value: this.formatNumber(this.analysis.amplitudeQty), unit: "吨", sub: this.formatPercent(this.analysis.amplitudeRate), className: "warn" },
        { label: "异常周数", value: this.analysis.abnormalWeekCount || 0, unit: "周", sub: this.getWeeklyThresholdText(), className: "danger" },
      ];
    },
  },
  mounted () {
    this.loadFilterOptions();
    this.loadData();
  },
  beforeDestroy () {
    clearTimeout(this.customerSearchTimer);
    clearTimeout(this.varSearchTimer);
  },
  methods: {
    async loadData () {
      this.loading = true;
      try {
        const res = await getWeeklyAnalysis(this.buildParams());
        if (res.data && res.data.success) {
          const data = res.data.data || {};
          this.analysis = data;
          this.trendList = data.trendList || [];
          this.abnormalWeeks = data.abnormalWeeks || [];
          this.page.currentPage = 1;
        } else {
          this.$message.error((res.data && res.data.msg) || "周度波动分析查询失败");
          this.clearData();
        }
      } catch (error) {
        console.error("周度波动分析查询失败:", error);
        this.$message.error("周度波动分析查询失败");
        this.clearData();
      } finally {
        this.loading = false;
      }
    },
    clearData () {
      this.analysis = {};
      this.trendList = [];
      this.abnormalWeeks = [];
    },
    buildParams () {
      const params = {
        ...this.query,
      };
      const tenantId = this.getCurrentTenantId();
      if (tenantId) {
        params.tenantId = tenantId;
      }
      if (this.dateRange && this.dateRange.length === 2) {
        params.startTime = this.dateRange[0];
        params.endTime = this.dateRange[1];
      }
      return params;
    },
    getCurrentTenantId () {
      const localTenantId = process.env.VUE_APP_LOCAL_TENANT_ID;
      if (localTenantId) {
        return localTenantId;
      }
      const userState = this.$store && this.$store.state && this.$store.state.user;
      return (userState && userState.tenantId) || "";
    },
    handleDateChange () {
      if (!this.dateRange || this.dateRange.length !== 2) {
        return;
      }
      this.loadData();
    },
    loadFilterOptions () {
      Promise.all([
        this.loadCustomers(""),
        this.loadVars(""),
      ]).catch(() => {});
    },
    async loadCustomers (keyword = "") {
      const query = (keyword || "").trim();
      this.customerLoading = true;
      try {
        const rows = unwrapList(await getCustomerOptions(query))
          .filter(item => item && item.id)
          .map(item => ({
            custNo: String(item.id),
            custName: item.deptName || String(item.id),
          }));
        if (!query) {
          this.defaultCustomerOptions = rows;
        }
        this.customerOptions = rows;
      } catch (error) {
        if (!query) {
          this.defaultCustomerOptions = [];
        }
        this.customerOptions = [];
      } finally {
        this.customerLoading = false;
      }
    },
    async loadVars (keyword = "") {
      const query = (keyword || "").trim();
      this.varLoading = true;
      try {
        const rows = unwrapList(await getVarOptions(query))
          .filter(item => item && item.varNo)
          .map(item => ({
            varNo: item.varNo,
            varName: item.varName || item.varNo,
          }));
        if (!query) {
          this.defaultVarOptions = rows;
        }
        this.varOptions = rows;
      } catch (error) {
        if (!query) {
          this.defaultVarOptions = [];
        }
        this.varOptions = [];
      } finally {
        this.varLoading = false;
      }
    },
    filterCustomers (keyword) {
      const query = (keyword || "").trim();
      clearTimeout(this.customerSearchTimer);
      if (!query) {
        this.customerOptions = this.defaultCustomerOptions.slice();
        return;
      }
      this.customerSearchTimer = setTimeout(() => {
        this.loadCustomers(query);
      }, 300);
    },
    filterVars (keyword) {
      const query = (keyword || "").trim();
      clearTimeout(this.varSearchTimer);
      if (!query) {
        this.varOptions = this.defaultVarOptions.slice();
        return;
      }
      this.varSearchTimer = setTimeout(() => {
        this.loadVars(query);
      }, 300);
    },
    resetQuery () {
      this.query = {
        balanceCustNo: "",
        varNo: "",
        vehicleNo: "",
      };
      this.dateRange = this.getDefaultDateRange();
      this.loadData();
    },
    openDetail (row) {
      this.activeWeek = row;
      this.detailVisible = true;
      this.detailPage.currentPage = 1;
      this.loadDetail();
    },
    closeDetail () {
      this.activeWeek = null;
      this.detailRows = [];
      this.detailPage.total = 0;
    },
    async loadDetail () {
      if (!this.activeWeek) {
        return;
      }
      this.detailLoading = true;
      try {
        const params = {
          ...this.query,
          startTime: `${this.activeWeek.weekStart} 00:00:00`,
          endTime: `${this.activeWeek.weekEnd} 23:59:59`,
          current: this.detailPage.currentPage,
          size: this.detailPage.pageSize,
        };
        const tenantId = this.getCurrentTenantId();
        if (tenantId) {
          params.tenantId = tenantId;
        }
        const res = await getWeeklyDetailPage(params);
        if (res.data && res.data.success) {
          const data = res.data.data || {};
          this.detailRows = data.records || [];
          this.detailPage.total = data.total || 0;
        } else {
          this.$message.error((res.data && res.data.msg) || "检斤明细查询失败");
          this.detailRows = [];
          this.detailPage.total = 0;
        }
      } catch (error) {
        console.error("检斤明细查询失败:", error);
        this.$message.error("检斤明细查询失败");
        this.detailRows = [];
        this.detailPage.total = 0;
      } finally {
        this.detailLoading = false;
      }
    },
    getDefaultDateRange () {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 83);
      return [this.formatDate(start), this.formatDate(end)];
    },
    formatDate (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    formatText (value) {
      if (value === null || value === undefined || value === "") {
        return "-";
      }
      return value;
    },
    formatName (name, code) {
      return this.formatText(name || code);
    },
    formatOptionLabel (name, code) {
      if (name && code) {
        return `${name}（${code}）`;
      }
      return this.formatText(name || code);
    },
    getWeeklyThresholdText () {
      const wowValue = this.getWeeklyParamValue("truck_out.weekly.wow_pct");
      const avgValue = this.getWeeklyParamValue("truck_out.weekly.avg_pct");
      const parts = [];
      if (wowValue !== "") {
        parts.push(`环比下降阈值 ${wowValue}%`);
      }
      if (avgValue !== "") {
        parts.push(`低于均值阈值 ${avgValue}%`);
      }
      return parts.length ? parts.join("，") : "阈值未配置";
    },
    getWeeklyParamValue (paramName) {
      const list = (this.analysis && this.analysis.paramSummary) || [];
      const item = list.find(row => row && row.paramName === paramName);
      if (!item) {
        return "";
      }
      return item.effectiveValue || item.paramValue || item.defaultValue || "";
    },
    formatCount (value) {
      return Number(value || 0);
    },
    formatNumber (value) {
      const num = Number(value || 0);
      return num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
    formatPercent (value) {
      const num = Number(value || 0) * 100;
      return `${num > 0 ? "+" : ""}${num.toFixed(2)}%`;
    },
    getRateClass (value) {
      const num = Number(value || 0);
      if (num > 0) return "positive";
      if (num < 0) return "negative";
      return "";
    },
    getTrendKey (item, index) {
      const row = item || {};
      return `${row.weekBucket || "empty"}-${row.weekStart || "empty"}-${row.weekEnd || "empty"}-${index}`;
    },
    getBarHeight (value) {
      const max = Math.max.apply(null, this.trendList.map(item => Number(item.totalQty || 0)).concat([1]));
      const height = Math.max(24, Number(value || 0) / max * 220);
      return `${height}px`;
    },
  },
};
</script>

<style lang="scss" scoped>
.weekly-analysis {
  padding: 12px;
}

.filter-card,
.content-card,
.summary-card {
  border-radius: 4px;
}

.filter-card {
  margin-bottom: 12px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
}

.header-tip {
  color: #909399;
  font-size: 12px;
  font-weight: 400;
}

.query-form {
  display: flex;
  flex-wrap: wrap;
}

.summary-row {
  margin-bottom: 12px;
}

.summary-card {
  margin-bottom: 12px;
  min-height: 104px;
}

.summary-label {
  color: #606266;
  font-size: 13px;
  margin-bottom: 8px;
}

.summary-value {
  color: #303133;
  font-size: 23px;
  font-weight: 600;
  line-height: 1.2;
}

.summary-value span {
  color: #909399;
  font-size: 12px;
  font-weight: 400;
  margin-left: 3px;
}

.summary-value.warn,
.summary-value.positive {
  color: #e6a23c;
}

.summary-value.danger,
.summary-value.negative {
  color: #f56c6c;
}

.summary-sub {
  color: #909399;
  font-size: 12px;
  margin-top: 8px;
}

.content-card {
  margin-bottom: 12px;
}

.trend-chart {
  height: 302px;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  overflow-x: auto;
  padding: 30px 4px 4px;
}

.trend-legend {
  display: flex;
  justify-content: center;
  gap: 18px;
  color: #606266;
  font-size: 12px;
  padding: 8px 0 2px;
}

.trend-legend span {
  display: inline-flex;
  align-items: center;
}

.legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
}

.legend-dot--abnormal {
  background: #f56c6c;
}

.legend-dot--normal {
  background: #409eff;
}

.bar-item {
  min-width: 70px;
  text-align: center;
}

.bar {
  position: relative;
  width: 100%;
  background: #409eff;
  border-radius: 3px 3px 0 0;
}

.bar.abnormal {
  background: #f56c6c;
}

.bar span {
  position: absolute;
  top: -24px;
  left: 50%;
  transform: translateX(-50%);
  color: #606266;
  font-size: 12px;
  white-space: nowrap;
}

.bar-label {
  color: #606266;
  font-size: 12px;
  margin-top: 8px;
}

.pagination-wrap {
  padding-top: 12px;
  text-align: right;
}

.positive {
  color: #67c23a;
}

.negative {
  color: #f56c6c;
}

.drawer-body {
  padding: 0 16px 16px;
}
</style>
